import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Slider,
  FormControl,
  FormLabel,
} from "@mui/material";
import {
  Traffic as TrafficIcon,
  Warning as WarningIcon,
  Construction as ConstructionIcon,
  CarCrash as AccidentIcon,
  Speed as SpeedIcon,
  Timeline as TimelineIcon,
} from "@mui/icons-material";
import MapView from "./MapView";
import { config } from "../config";

function TrafficInfo() {
  const [traffic, setTraffic] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [currentPosition, setCurrentPosition] = useState(null);
  const [searchRadius, setSearchRadius] = useState(150); // km

  useEffect(() => {
    getCurrentPosition();
  }, []);

  useEffect(() => {
    if (currentPosition) {
      loadTrafficData();

      // Automatische Aktualisierung alle 2 Minuten
      const interval = setInterval(() => {
        console.log("Auto-refreshing traffic data...");
        loadTrafficData();
      }, 120000); // 2 Minuten

      return () => clearInterval(interval);
    }
  }, [currentPosition, searchRadius]);

  // GPS Position ermitteln
  const getCurrentPosition = () => {
    if (!navigator.geolocation) {
      setError("Geolocation wird nicht unterstützt");
      loadTrafficData(); // Fallback ohne GPS
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setCurrentPosition({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        });
      },
      (error) => {
        console.error("GPS error:", error);
        setError("GPS-Position konnte nicht ermittelt werden");
        loadTrafficData(); // Fallback ohne GPS
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000, // 5 Minuten Cache
      }
    );
  };

  const loadTrafficData = async () => {
    setLoading(true);
    setError("");

    try {
      let trafficUrl = config.getApiUrl("/traffic");
      let incidentsUrl = config.getApiUrl("/incidents");

      // GPS-basierte Bounding Box erstellen (KORREKTE Berechnung)
      if (currentPosition) {
        const { lat, lng } = currentPosition;

        // Korrekte Berechnung: 1 Grad Breite ≈ 111 km, 1 Grad Länge ≈ 111 * cos(lat) km
        const latRadiusInDegrees = searchRadius / 111.0;
        const lngRadiusInDegrees =
          searchRadius / (111.0 * Math.cos((lat * Math.PI) / 180));

        const bbox = `${lat - latRadiusInDegrees},${lng - lngRadiusInDegrees},${
          lat + latRadiusInDegrees
        },${lng + lngRadiusInDegrees}`;
        trafficUrl += `?bbox=${bbox}`;
        incidentsUrl += `?bbox=${bbox}`;

        console.log("TrafficInfo Debug (CORRECTED):", {
          currentPosition,
          searchRadius,
          latRadiusInDegrees,
          lngRadiusInDegrees,
          bbox,
          trafficUrl,
          incidentsUrl,
        });
      }

      // Traffic data laden
      const trafficResponse = await fetch(trafficUrl);
      const trafficData = await trafficResponse.json();

      console.log("Traffic API Response:", trafficData);

      if (trafficData.success) {
        setTraffic(trafficData.traffic || trafficData.data || []);
      } else {
        console.error("Traffic API Error:", trafficData.error);
      }

      // Incidents laden
      const incidentsResponse = await fetch(incidentsUrl);
      const incidentsData = await incidentsResponse.json();

      console.log("Incidents API Response:", incidentsData);

      if (incidentsData.success) {
        // Filtere Incidents im Umkreis
        let filteredIncidents = incidentsData.incidents || [];

        console.log("Raw incidents:", filteredIncidents);

        if (currentPosition && filteredIncidents.length > 0) {
          filteredIncidents = filteredIncidents.filter((incident) => {
            // Konvertiere Koordinaten zu Zahlen (falls sie als Strings kommen)
            const incidentLat = parseFloat(incident.lat);
            const incidentLng = parseFloat(incident.lng);

            const distance = calculateDistance(
              currentPosition.lat,
              currentPosition.lng,
              incidentLat,
              incidentLng
            );
            console.log(
              `Incident distance: ${distance}km, radius: ${searchRadius}km`
            );
            return distance <= searchRadius;
          });
        }

        console.log("Filtered incidents:", filteredIncidents);
        setIncidents(filteredIncidents);
      } else {
        console.error("Incidents API Error:", incidentsData.error);
      }
    } catch (error) {
      console.error("Traffic data error:", error);
      setError("Fehler beim Laden der Verkehrsdaten");
    } finally {
      setLoading(false);
    }
  };

  // Distanz zwischen zwei GPS-Punkten berechnen (Haversine-Formel)
  const calculateDistance = (lat1, lng1, lat2, lng2) => {
    const R = 6371; // Erdradius in km
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLng = ((lng2 - lng1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLng / 2) *
        Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  const getIncidentIcon = (type) => {
    switch (type) {
      case "traffic_jam":
        return <TrafficIcon color="error" />;
      case "accident":
        return <AccidentIcon color="error" />;
      case "construction":
        return <ConstructionIcon color="warning" />;
      case "road_closed":
        return <WarningIcon color="error" />;
      default:
        return <WarningIcon color="action" />;
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case "low":
        return "success";
      case "medium":
        return "warning";
      case "high":
        return "error";
      case "critical":
        return "error";
      default:
        return "default";
    }
  };

  const getIncidentTitle = (type) => {
    const titles = {
      traffic_jam: "Verkehrsstau",
      accident: "Unfall",
      construction: "Baustelle",
      road_closed: "Straße gesperrt",
      hazard: "Gefahr",
    };
    return titles[type] || "Verkehrsvorfall";
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", height: "100%" }}>
      {/* Sidebar */}
      <Paper sx={{ width: 400, p: 2, overflow: "auto" }}>
        <Typography variant="h5" gutterBottom>
          Verkehrsinformationen
        </Typography>

        {currentPosition && (
          <Alert severity="success" sx={{ mb: 2 }}>
            📍 GPS aktiv - Umkreis: {searchRadius}km
            <br />
            Position: {currentPosition.lat.toFixed(4)},{" "}
            {currentPosition.lng.toFixed(4)}
          </Alert>
        )}

        {/* Radius-Einstellung */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <FormControl fullWidth>
              <FormLabel sx={{ mb: 2 }}>
                Suchradius: {searchRadius} km
              </FormLabel>
              <Slider
                value={searchRadius}
                onChange={(event, newValue) => setSearchRadius(newValue)}
                min={10}
                max={300}
                step={10}
                marks={[
                  { value: 25, label: "25km" },
                  { value: 50, label: "50km" },
                  { value: 100, label: "100km" },
                  { value: 150, label: "150km" },
                  { value: 200, label: "200km" },
                  { value: 300, label: "300km" },
                ]}
                valueLabelDisplay="auto"
              />
            </FormControl>
          </CardContent>
        </Card>

        {/* Verkehrskarte Legende */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              🗺️ Karten-Legende
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <div
                  style={{
                    width: 16,
                    height: 16,
                    borderRadius: "50%",
                    backgroundColor: "#4CAF50",
                    border: "2px solid white",
                  }}
                ></div>
                <Typography variant="body2">
                  Flüssiger Verkehr (&gt;80%)
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <div
                  style={{
                    width: 16,
                    height: 16,
                    borderRadius: "50%",
                    backgroundColor: "#FF9800",
                    border: "2px solid white",
                  }}
                ></div>
                <Typography variant="body2">
                  Langsamer Verkehr (60-80%)
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <div
                  style={{
                    width: 16,
                    height: 16,
                    borderRadius: "50%",
                    backgroundColor: "#F44336",
                    border: "2px solid white",
                  }}
                ></div>
                <Typography variant="body2">Stau (30-60%)</Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <div
                  style={{
                    width: 16,
                    height: 16,
                    borderRadius: "50%",
                    backgroundColor: "#9C27B0",
                    border: "2px solid white",
                  }}
                ></div>
                <Typography variant="body2">Blockiert (&lt;30%)</Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {config.isDevelopment && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Live-Modus: Zeigt echte Verkehrsdaten
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Verkehrsstatistiken */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6}>
            <Card>
              <CardContent sx={{ textAlign: "center", py: 2 }}>
                <SpeedIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
                <Typography variant="h6">
                  {incidents.filter((i) => i.type === "traffic_jam").length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Staus aktiv
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6}>
            <Card>
              <CardContent sx={{ textAlign: "center", py: 2 }}>
                <ConstructionIcon
                  color="warning"
                  sx={{ fontSize: 40, mb: 1 }}
                />
                <Typography variant="h6">
                  {incidents.filter((i) => i.type === "construction").length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Baustellen
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Aktuelle Verkehrsvorfälle */}
        <Typography variant="h6" gutterBottom>
          Aktuelle Vorfälle
        </Typography>

        {incidents.length === 0 ? (
          <Alert severity="success">
            Keine aktuellen Verkehrsvorfälle gemeldet
          </Alert>
        ) : (
          <List>
            {incidents.map((incident) => (
              <ListItem key={incident.id} divider>
                <ListItemIcon>{getIncidentIcon(incident.type)}</ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography variant="subtitle2">
                        {getIncidentTitle(incident.type)}
                      </Typography>
                      <Chip
                        label={incident.severity}
                        color={getSeverityColor(incident.severity)}
                        size="small"
                      />
                    </Box>
                  }
                  secondary={
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        {incident.description}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {incident.location ||
                          `${parseFloat(incident.lat).toFixed(4)}, ${parseFloat(
                            incident.lng
                          ).toFixed(4)}`}
                      </Typography>
                      {config.isDevelopment && (
                        <Typography
                          variant="caption"
                          color="primary"
                          sx={{ display: "block" }}
                        >
                          Demo-Daten
                        </Typography>
                      )}
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>
        )}

        {/* Verkehrsfluss-Informationen */}
        {traffic.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              Verkehrsfluss
            </Typography>
            <List>
              {traffic.slice(0, 5).map((segment, index) => (
                <ListItem key={index} divider>
                  <ListItemIcon>
                    <TimelineIcon
                      color={
                        segment.speed < 30
                          ? "error"
                          : segment.speed < 50
                          ? "warning"
                          : "success"
                      }
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={segment.road_name || `Segment ${index + 1}`}
                    secondary={
                      <Box>
                        <Typography variant="body2">
                          Geschwindigkeit: {segment.speed} km/h
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Status:{" "}
                          {segment.speed < 30
                            ? "Stau"
                            : segment.speed < 50
                            ? "Langsam"
                            : "Flüssig"}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}
      </Paper>

      {/* Karte */}
      <Box sx={{ flex: 1 }}>
        <MapView
          incidents={incidents}
          traffic={traffic}
          center={
            currentPosition
              ? [currentPosition.lat, currentPosition.lng]
              : [51.1657, 10.4515]
          }
          zoom={currentPosition ? 10 : 6}
          currentPosition={currentPosition}
        />
      </Box>
    </Box>
  );
}

export default TrafficInfo;
